// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

export const UPLOAD_DOCUMENT_MAX_FILE_BYTES = 5 * 1024 * 1024;
export const UPLOAD_DOCUMENT_MAX_BATCH_BYTES = 20 * 1024 * 1024;
export const UPLOAD_DOCUMENT_MAX_FILE_COUNT = 10;

const ALLOWED_EXTENSIONS = new Set([
  'pdf',
  'jpg',
  'jpeg',
  'png',
  'xlsx',
  'docx',
  'csv',
]);

const ALLOWED_MIME_TYPES = new Set([
  'application/pdf',
  'image/jpeg',
  'image/png',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/csv',
  'application/csv',
]);

export type UploadFileValidationError =
  | 'missing'
  | 'invalid_type'
  | 'too_large'
  | 'batch_too_large'
  | 'too_many_files';

export interface UploadFileValidationResult {
  readonly valid: boolean;
  readonly error?: UploadFileValidationError;
}

export interface UploadFilesValidationResult {
  readonly valid: boolean;
  readonly error?: UploadFileValidationError;
  readonly validFiles: File[];
}

const getExtension = (fileName: string): string => {
  const extension = fileName.split('.').pop()?.trim().toLowerCase();

  return extension ?? '';
};

export const isAllowedUploadFileType = (file: File): boolean => {
  const extension = getExtension(file.name);

  if (ALLOWED_EXTENSIONS.has(extension)) {
    return true;
  }

  return file.type !== '' && ALLOWED_MIME_TYPES.has(file.type);
};

export const validateUploadFile = (
  file: File | null | undefined,
): UploadFileValidationResult => {
  if (!file) {
    return { valid: false, error: 'missing' };
  }

  if (!isAllowedUploadFileType(file)) {
    return { valid: false, error: 'invalid_type' };
  }

  if (file.size > UPLOAD_DOCUMENT_MAX_FILE_BYTES) {
    return { valid: false, error: 'too_large' };
  }

  return { valid: true };
};

export const sumFileSizes = (files: readonly File[]): number =>
  files.reduce((total, file) => total + file.size, 0);

export const validateUploadFiles = (
  files: readonly File[],
): UploadFilesValidationResult => {
  if (files.length === 0) {
    return { valid: false, error: 'missing', validFiles: [] };
  }

  if (files.length > UPLOAD_DOCUMENT_MAX_FILE_COUNT) {
    return { valid: false, error: 'too_many_files', validFiles: [] };
  }

  const validFiles: File[] = [];

  for (const file of files) {
    const validation = validateUploadFile(file);

    if (!validation.valid) {
      return {
        valid: false,
        error: validation.error ?? 'invalid_type',
        validFiles: [],
      };
    }

    validFiles.push(file);
  }

  if (sumFileSizes(validFiles) > UPLOAD_DOCUMENT_MAX_BATCH_BYTES) {
    return { valid: false, error: 'batch_too_large', validFiles: [] };
  }

  return { valid: true, validFiles };
};

export const mergeUploadFiles = (
  currentFiles: readonly File[],
  incomingFiles: FileList | null,
): UploadFilesValidationResult => {
  if (!incomingFiles?.length) {
    return validateUploadFiles(currentFiles);
  }

  const merged = [...currentFiles];

  for (const file of Array.from(incomingFiles)) {
    const isDuplicate = merged.some(
      existing =>
        existing.name === file.name &&
        existing.size === file.size &&
        existing.lastModified === file.lastModified,
    );

    if (!isDuplicate) {
      merged.push(file);
    }
  }

  return validateUploadFiles(merged);
};
